export interface RGB {
    r: number;
    g: number;
    b: number;
}

/** The site's palette: black/white, plus the RGB primaries. */
export const NOISE_COLORS: RGB[] = [
    { r: 0, g: 0, b: 0 },
    { r: 255, g: 0, b: 0 },
    { r: 0, g: 255, b: 0 },
    { r: 0, g: 0, b: 255 }
];

export const pickNoiseColor = (): RGB =>
    NOISE_COLORS[Math.floor(Math.random() * NOISE_COLORS.length)];

/** True when the visitor has asked their OS to reduce motion. */
export const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface NoiseOptions {
    color?: RGB;
    /** Size of one noise block in device pixels. */
    pixelSize?: number;
    /** Redraws per second. */
    fps?: number;
    /** 0–1, applied to the whole layer. */
    alpha?: number;
}

/**
 * Renders animated static onto a canvas and returns a stop function.
 *
 * This replaces five near-identical copies of the same loop. The original
 * built a full-resolution ImageData every frame with a four-deep nested
 * loop — for a 300x300 card that is 90,000 pixels x 4 channel writes,
 * ten times a second, per hovered card.
 *
 * Here the noise is generated at 1/pixelSize resolution on a small
 * offscreen canvas and then scaled up with image smoothing disabled. The
 * result is pixel-identical (the original wrote identical values across
 * each pixelSize block anyway) for 1/16th of the work at pixelSize 4.
 *
 * Returns a no-op stop function and paints a single static frame when the
 * visitor prefers reduced motion.
 */
export const startNoise = (
    canvas: HTMLCanvasElement,
    {
        color = { r: 0, g: 0, b: 0 },
        pixelSize = 4,
        fps = 10,
        alpha = 1
    }: NoiseOptions = {}
): (() => void) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return () => {};

    const width = canvas.width;
    const height = canvas.height;
    if (width === 0 || height === 0) return () => {};

    const cols = Math.max(1, Math.ceil(width / pixelSize));
    const rows = Math.max(1, Math.ceil(height / pixelSize));

    const offscreen = document.createElement('canvas');
    offscreen.width = cols;
    offscreen.height = rows;
    const offCtx = offscreen.getContext('2d');
    if (!offCtx) return () => {};

    const imageData = offCtx.createImageData(cols, rows);
    const data = imageData.data;
    const alphaByte = Math.round(255 * alpha);

    const drawFrame = () => {
        for (let i = 0; i < data.length; i += 4) {
            const useWhite = Math.random() > 0.5;
            data[i] = useWhite ? 255 : color.r;
            data[i + 1] = useWhite ? 255 : color.g;
            data[i + 2] = useWhite ? 255 : color.b;
            data[i + 3] = alphaByte;
        }
        offCtx.putImageData(imageData, 0, 0);

        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(offscreen, 0, 0, cols, rows, 0, 0, width, height);
    };

    // One still frame, no loop, no strobing.
    if (prefersReducedMotion()) {
        drawFrame();
        return () => {};
    }

    let frameId = 0;
    let running = true;
    let lastDraw = 0;
    const interval = 1000 / fps;

    const tick = (now: number) => {
        if (!running) return;
        if (now - lastDraw >= interval) {
            lastDraw = now;
            drawFrame();
        }
        frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    // Each caller owns exactly one stopper, so a fast mouse moving across
    // a grid can no longer strand loops that nothing ever cancels.
    return () => {
        running = false;
        cancelAnimationFrame(frameId);
    };
};

/**
 * Draws an image with a static-noise overlay and scattered colour dots.
 * Used for book covers, where the noise sits over artwork rather than
 * replacing it.
 */
export const drawNoisyImage = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    size: number,
    dotCount = 15
) => {
    ctx.drawImage(img, 0, 0, size, size);

    const imageData = ctx.getImageData(0, 0, size, size);
    const data = imageData.data;

    for (let i = 3; i < data.length; i += 4) {
        data[i] = Math.random() * 255 * 0.3;
    }

    const primaries = [
        [255, 0, 0],
        [255, 255, 0],
        [0, 0, 255]
    ];

    for (let i = 0; i < dotCount; i++) {
        const x = Math.floor(Math.random() * size);
        const y = Math.floor(Math.random() * size);
        const color = primaries[Math.floor(Math.random() * primaries.length)];
        const base = (y * size + x) * 4;

        for (let dy = 0; dy < 2; dy++) {
            for (let dx = 0; dx < 2; dx++) {
                const index = base + (dy * size + dx) * 4;
                if (index < data.length - 3) {
                    data[index] = color[0];
                    data[index + 1] = color[1];
                    data[index + 2] = color[2];
                    data[index + 3] = 255;
                }
            }
        }
    }

    ctx.putImageData(imageData, 0, 0);
};

/** Fallback pattern for covers whose image fails to load. */
export const drawDefaultPattern = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
) => {
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 2;
    for (let i = 0; i < width + height; i += 20) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(0, i);
        ctx.stroke();
    }
};
